import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import es from './locales/es.json';
import en from './locales/en.json';
import it from './locales/it.json';
import ro from './locales/ro.json';
import bg from './locales/bg.json';
import el from './locales/el.json';
import { getUserLanguagePref, setUserLanguagePref, getUsername, migrateLanguagePref } from '../services/storage';

// ES y EN son la base: están siempre disponibles para cualquier perfil. El
// resto son los idiomas de los países del flujo nuevo y solo se ofrecen a quien
// opera en ellos (ver languagesForCountries).
export const SUPPORTED_LANGUAGES = ['es', 'en', 'it', 'ro', 'bg', 'el'] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Idiomas siempre disponibles, sea cual sea el país del usuario.
const BASE_LANGUAGES: AppLanguage[] = ['es', 'en'];

// País (ISO-2) → idioma. OJO: el código del griego es 'el', no 'gr'.
// Pakistán (urdu) y Egipto (árabe) NO están: son idiomas RTL y requieren
// espejar el layout, trabajo que se dejó fuera de alcance a propósito. Esos
// usuarios se quedan en inglés, que es el idioma de trabajo habitual en logística.
const COUNTRY_LANGUAGES: Record<string, AppLanguage> = {
  IT: 'it',
  RO: 'ro',
  BG: 'bg',
  GR: 'el',
};

// Idioma de la app → locale BCP-47 para fechas, horas y números.
//
// Antes esto se resolvía con un ternario `startsWith('en') ? 'en-GB' : 'es-ES'`
// repetido en 10 sitios. Con solo dos idiomas funcionaba, pero al añadir más
// TODO lo que no fuera inglés caía en español (el calendario salía en español
// para un italiano). Se centraliza aquí para que añadir un idioma sea una línea.
const DATE_LOCALES: Record<AppLanguage, string> = {
  es: 'es-ES',
  en: 'en-GB',
  it: 'it-IT',
  ro: 'ro-RO',
  bg: 'bg-BG',
  el: 'el-GR',
};

/**
 * Locale a usar en toLocaleDateString / toLocaleString según el idioma activo.
 * Acepta idiomas con región ("es-ES") y cae a inglés si no lo conocemos.
 */
export function getDateLocale(lang?: string): string {
  const base = (lang ?? i18n.language ?? 'en').split('-')[0] as AppLanguage;
  return DATE_LOCALES[base] ?? 'en-GB';
}

/**
 * Idiomas que se ofrecen a un usuario: los de sus países + ES/EN.
 * Así un controlador de Grecia ve griego, pero no se le llena el selector con
 * idiomas de países en los que no opera.
 */
export function languagesForCountries(countryCodes: string[]): AppLanguage[] {
  const fromCountries = countryCodes
    .map((c) => COUNTRY_LANGUAGES[c?.toUpperCase()])
    .filter((l): l is AppLanguage => !!l);
  // Set para no repetir si el usuario tiene varios países del mismo idioma.
  return [...new Set([...BASE_LANGUAGES, ...fromCountries])];
}

// Limpia la preferencia GLOBAL heredada (versiones antiguas persistían el toggle
// del login en una sola clave que bloqueaba la resolución por perfil). Ahora la
// preferencia manual se guarda POR USUARIO (APP_LANG:<email>).
migrateLanguagePref();

void i18n.use(initReactI18next).init({
  resources: {
    es: { translation: es },
    en: { translation: en },
    it: { translation: it },
    ro: { translation: ro },
    bg: { translation: bg },
    el: { translation: el },
  },
  // Al arrancar: si hay sesión y ese usuario eligió idioma, se usa (sin parpadeo
  // al recargar). Si no, inglés; tras el login, resolveLanguageForUser lo ajusta
  // según el perfil (self-managed → en, hispanohablante → es).
  lng: getUserLanguagePref(getUsername()) ?? 'en',
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
});

// Mantén el atributo lang del <html> sincronizado con el idioma activo. El
// calendario nativo de <input type="date"> (días de la semana y meses) toma su
// idioma de este atributo, no de i18n: sin esto, con la app en inglés el
// calendario seguía mostrándose en español. Se aplica al arrancar y en cada cambio.
function syncDocumentLang(lang: string) {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
}
syncDocumentLang(i18n.language);
i18n.on('languageChanged', syncDocumentLang);

// Cambia el idioma y lo persiste como preferencia MANUAL de ese usuario. A partir
// de aquí, ese usuario recuerda su elección (prevalece sobre el idioma por país).
// Lo usan el selector de Ajustes y los botones EN/ES del header.
export function changeLanguage(user: string | null, lang: AppLanguage) {
  if (user) setUserLanguagePref(user, lang);
  void i18n.changeLanguage(lang);
}

// Elección hecha en el toggle del login (aún no hay sesión). Se recuerda en un
// módulo hasta que el login la confirma para el usuario que entra (así se persiste
// bajo su email, no bajo uno equivocado). No usamos localStorage aquí para no
// ensuciar nada si el usuario cambia de idea sin llegar a entrar.
let pendingLoginLang: AppLanguage | null = null;

// Cambia el idioma de la PANTALLA de login y recuerda la elección para aplicarla
// al usuario cuando inicie sesión.
export function selectLoginLanguage(lang: AppLanguage) {
  pendingLoginLang = lang;
  void i18n.changeLanguage(lang);
}

// El login la consume al autenticar: devuelve la elección del toggle (o null) y la
// limpia, para persistirla bajo el email del usuario que entra.
export function consumePendingLoginLanguage(): AppLanguage | null {
  const lang = pendingLoginLang;
  pendingLoginLang = null;
  return lang;
}

// Idioma por defecto de la pantalla de login: inglés, salvo que el usuario acabe
// de elegir uno en el toggle. Se llama al montar el login.
export function applyLoginDefaultLanguage() {
  if (!pendingLoginLang) void i18n.changeLanguage('en');
}

// Al iniciar sesión / rehidratar, aplica el idioma del usuario: su preferencia
// manual si eligió una alguna vez; si no, el idioma por defecto según su perfil
// (inglés para self-managed y no hispanohablantes, español para hispanohablantes).
// Como la preferencia es por usuario, tras un logout el siguiente usuario NO
// hereda el idioma del anterior: sin elección propia → idioma por su país.
export function resolveLanguageForUser(user: string | null, spanishSpeaking: boolean) {
  const manual = getUserLanguagePref(user);
  void i18n.changeLanguage(manual ?? (spanishSpeaking ? 'es' : 'en'));
}

export default i18n;
